// src/pages/Estimation.jsx
import React, { useState, useEffect, useCallback } from "react";
import { supabase } from "../lib/supabaseClient";
import { Link, useParams, useNavigate } from "react-router-dom";
import { FiUserPlus } from "react-icons/fi";
import { useTranslation } from "react-i18next";
import { withoutProposalWorks } from "../lib/proposalExclusion";

const ESTIMATION_METHODS = [
  { value: "Estimation IA", key: "ai" },
  { value: "Estimation physique", key: "physical" },
  { value: "Estimation semblables", key: "comparables" },
  { value: "Estimation assurance", key: "insurance" },
  { value: "Estimation succession", key: "succession" },
];

export default function EstimationComponent() {
  const { artworkId } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const [estimations, setEstimations] = useState([]);
  const [artworks, setArtworks] = useState([]);
  const [experts, setExperts] = useState([]);
  const [userId, setUserId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [documentFile, setDocumentFile] = useState(null);

  const [newEstimation, setNewEstimation] = useState({
    artwork: artworkId || "",
    artist: "",
    date: new Date().toISOString().split("T")[0],
    estimatedValue: "",
    appraiser: "",
    method: "Estimation physique",
    notes: "",
    document_url: "",
  });

  const locale = i18n.resolvedLanguage || i18n.language || "fr";

  const formatDate = useCallback(
    (value) => (value ? new Intl.DateTimeFormat(locale).format(new Date(value)) : ""),
    [locale]
  );

  const formatCurrency = useCallback(
    (value) => {
      const amount = Number(value || 0);
      return new Intl.NumberFormat(locale, {
        style: "currency",
        currency: "EUR",
        maximumFractionDigits: 2,
      }).format(Number.isFinite(amount) ? amount : 0);
    },
    [locale]
  );

  const methodLabel = useCallback(
    (value) => {
      const method = ESTIMATION_METHODS.find((item) => item.value === value);
      return method ? t(`estimationPage.methods.${method.key}.label`) : value || "—";
    },
    [t]
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        const {
          data: { session },
          error: sessionError,
        } = await supabase.auth.getSession();
        if (sessionError) throw sessionError;
        if (!session) {
          navigate("/login");
          return;
        }

        setUserId(session.user.id);

        const { data: artworksData, error: artworksError } = await withoutProposalWorks(supabase
          .from("artworks")
          .select("id, title, artist")
          .eq("client_id", session.user.id), session.user.id);
        if (artworksError) throw artworksError;
        const artworkRows = artworksData || [];
        setArtworks(artworkRows);

        if (artworkRows.length === 0) {
          navigate("/collections");
          return;
        }

        const { data: expertsData, error: expertsError } = await supabase
          .from("experts")
          .select("name")
          .eq("client_id", session.user.id);
        if (expertsError) throw expertsError;
        setExperts(expertsData || []);

        const { data: estimationsData, error: estimationsError } = await supabase
          .from("estimations")
          .select("*")
          .eq("client_id", session.user.id);
        if (estimationsError) throw estimationsError;

        const formatted = (estimationsData || []).map((estimation) => {
          const matchedArtwork = artworkRows.find(
            (artwork) => artwork.id === estimation.artwork_id
          );
          return {
            id: estimation.id,
            artwork:
              matchedArtwork?.title || t("estimationPage.fallbacks.unknownArtwork"),
            artist:
              matchedArtwork?.artist || t("estimationPage.fallbacks.unknownArtist"),
            date: estimation.date || "",
            estimatedValue: estimation.estimated_value,
            appraiser: estimation.appraiser || "",
            method: estimation.method,
            notes: estimation.notes,
            document_url: estimation.document_url || null,
          };
        });

        setEstimations(formatted);
      } catch (error) {
        console.error("[Estimation] load error:", error);
      }
    };

    fetchData();
  }, [artworkId, navigate, t]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!userId) return;

    if (!newEstimation.artwork || !newEstimation.estimatedValue) {
      window.alert(t("estimationPage.alerts.required"));
      return;
    }

    let uploadedUrl = "";
    if (documentFile) {
      try {
        const fileExt = documentFile.name.split(".").pop();
        const fileName = `estimation-${Date.now()}.${fileExt}`;
        const { error: uploadError } = await supabase.storage
          .from("documents")
          .upload(fileName, documentFile);
        if (uploadError) throw uploadError;

        const { data: urlData } = supabase.storage
          .from("documents")
          .getPublicUrl(fileName);
        uploadedUrl = urlData.publicUrl;
      } catch (uploadError) {
        window.alert(t("estimationPage.alerts.uploadError"));
        console.error(uploadError);
        return;
      }
    }

    const estimationData = {
      artwork_id: newEstimation.artwork,
      estimated_value: parseFloat(newEstimation.estimatedValue),
      date: newEstimation.date,
      appraiser: newEstimation.appraiser || null,
      method: newEstimation.method,
      notes: newEstimation.notes,
      document_url: uploadedUrl,
      client_id: userId,
    };

    const { error: insertError } = await supabase
      .from("estimations")
      .insert(estimationData);

    if (insertError) {
      console.error("[Estimation] save error:", insertError);
      window.alert(
        t("estimationPage.alerts.saveError", { message: insertError.message })
      );
    } else {
      window.alert(t("estimationPage.alerts.saved"));
      setShowForm(false);
      window.location.reload();
    }
  };

  const filteredEstimations = estimations.filter((estimation) =>
    estimation.artwork.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="max-w-6xl mx-auto px-6 py-12">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-serif">{t("estimationPage.title")}</h1>
        <button
          onClick={() => setShowForm(!showForm)}
          className="btn-artsy btn-primary-artsy"
        >
          {showForm
            ? t("estimationPage.actions.cancel")
            : t("estimationPage.actions.newEstimation")}
        </button>
      </div>

      {showForm && (
        <form
          onSubmit={handleSubmit}
          className="border p-6 rounded bg-white mb-10 space-y-4"
        >
          <h2 className="text-xl font-serif mb-4">
            {t("estimationPage.form.title")}
          </h2>

          <div className="text-sm text-gray-600 mb-4 space-y-2">
            <p>{t("estimationPage.form.methodIntro")}</p>
            {ESTIMATION_METHODS.map((method) => (
              <p key={method.value}>
                <strong>
                  • {t(`estimationPage.methods.${method.key}.label`)}
                </strong>{" "}
                {t(`estimationPage.methods.${method.key}.description`)}
              </p>
            ))}
          </div>

          <div>
            <label className="block mb-1 text-sm font-medium">
              {t("estimationPage.fields.artwork")}
            </label>
            <select
              className="border w-full p-2 rounded"
              value={newEstimation.artwork}
              onChange={(event) =>
                setNewEstimation({
                  ...newEstimation,
                  artwork: event.target.value,
                })
              }
            >
              <option value="">{t("estimationPage.placeholders.selectArtwork")}</option>
              {artworks.map((artwork) => (
                <option key={artwork.id} value={artwork.id}>
                  {artwork.title}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block mb-1 text-sm font-medium">
              {t("estimationPage.fields.appraiser")}
            </label>
            <div className="flex gap-2 items-center">
              <select
                className="border p-2 rounded w-full"
                value={newEstimation.appraiser}
                onChange={(event) =>
                  setNewEstimation({
                    ...newEstimation,
                    appraiser: event.target.value,
                  })
                }
              >
                <option value="">{t("estimationPage.placeholders.selectExpert")}</option>
                {experts.map((expert, index) => (
                  <option key={index} value={expert.name}>
                    {expert.name}
                  </option>
                ))}
              </select>
              <Link
                to="/add-expert"
                className="p-2 rounded bg-blue-600 text-white hover:bg-blue-700 flex items-center"
              >
                <FiUserPlus className="mr-1" />
                {t("estimationPage.actions.addExpert")}
              </Link>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block mb-1 text-sm font-medium">
                {t("estimationPage.fields.date")}
              </label>
              <input
                type="date"
                className="border w-full p-2 rounded"
                value={newEstimation.date}
                onChange={(event) =>
                  setNewEstimation({ ...newEstimation, date: event.target.value })
                }
              />
            </div>
            <div>
              <label className="block mb-1 text-sm font-medium">
                {t("estimationPage.fields.estimatedValue")}
              </label>
              <input
                type="number"
                className="border w-full p-2 rounded"
                value={newEstimation.estimatedValue}
                onChange={(event) =>
                  setNewEstimation({
                    ...newEstimation,
                    estimatedValue: event.target.value,
                  })
                }
              />
            </div>
            <div>
              <label className="block mb-1 text-sm font-medium">
                {t("estimationPage.fields.method")}
              </label>
              <select
                className="border w-full p-2 rounded"
                value={newEstimation.method}
                onChange={(event) =>
                  setNewEstimation({
                    ...newEstimation,
                    method: event.target.value,
                  })
                }
              >
                {ESTIMATION_METHODS.map((method) => (
                  <option key={method.value} value={method.value}>
                    {t(`estimationPage.methods.${method.key}.label`)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block mb-1 text-sm font-medium">
              {t("estimationPage.fields.notes")}
            </label>
            <textarea
              className="border w-full p-2 rounded"
              rows="3"
              value={newEstimation.notes}
              onChange={(event) =>
                setNewEstimation({ ...newEstimation, notes: event.target.value })
              }
            />
          </div>

          <div>
            <label className="block mb-1 text-sm font-medium">
              {t("estimationPage.fields.document")}
            </label>
            <input
              type="file"
              accept="application/pdf,image/*"
              onChange={(event) => setDocumentFile(event.target.files?.[0] || null)}
              className="border w-full p-2 rounded"
            />
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              type="submit"
              className="bg-black text-white py-2 px-4 rounded hover:bg-gray-800"
            >
              {t("estimationPage.actions.save")}
            </button>
            <Link
              to="/estimation-semblables"
              className="inline-block bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-4 rounded"
            >
              {t("estimationPage.actions.comparableEstimation")}
            </Link>
            <Link
              to="/estimation-point"
              className="inline-block bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-4 rounded"
            >
              {t("estimationPage.actions.pointsEstimation")}
            </Link>
          </div>
        </form>
      )}

      <input
        type="text"
        placeholder={t("estimationPage.placeholders.searchArtwork")}
        className="border p-2 mb-6 w-full rounded"
        value={searchQuery}
        onChange={(event) => setSearchQuery(event.target.value)}
      />

      <h2 className="text-xl font-serif mb-4">
        {t("estimationPage.history.title")}
      </h2>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-medium">
          <thead>
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-dark">
                {t("estimationPage.fields.artwork")}
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-dark">
                {t("estimationPage.fields.artist")}
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-dark">
                {t("estimationPage.fields.date")}
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-dark">
                {t("estimationPage.fields.estimatedValueShort")}
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-dark">
                {t("estimationPage.fields.appraiser")}
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-dark">
                {t("estimationPage.fields.method")}
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-dark">
                {t("estimationPage.fields.documentShort")}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-medium">
            {filteredEstimations.map((estimation) => (
              <tr key={estimation.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 whitespace-nowrap">{estimation.artwork}</td>
                <td className="px-4 py-3 whitespace-nowrap">{estimation.artist}</td>
                <td className="px-4 py-3 whitespace-nowrap">
                  {formatDate(estimation.date)}
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  {formatCurrency(estimation.estimatedValue)}
                </td>
                <td className="px-4 py-3 whitespace-nowrap">{estimation.appraiser}</td>
                <td className="px-4 py-3 whitespace-nowrap">
                  {methodLabel(estimation.method)}
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  {estimation.document_url ? (
                    <a
                      href={estimation.document_url}
                      download
                      className="text-blue-600 underline flex items-center gap-1"
                    >
                      📄 {t("estimationPage.actions.download")}
                    </a>
                  ) : (
                    <span className="text-gray-400 text-sm">—</span>
                  )}
                </td>
              </tr>
            ))}
            {filteredEstimations.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-sm text-gray-500">
                  {t("estimationPage.history.empty")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export const Estimation = EstimationComponent;
